import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Button } from '@m/alchemy-ui/Button';
import { BarChart } from '@m/alchemy-ui/BarChart';
import { EmptyState as ErrorMessage } from '@m/alchemy-ui/esm/empty-state/EmptyState';
import { LoadingIndicator } from '@m/alchemy-ui/LoadingIndicator';
import { Select } from '@m/alchemy-ui/Select';
import { useIntl } from 'react-intl';
import { ThemeProvider } from '@m/alchemy-ui/theme';
import {
  type ChallengesQuery,
  type ChallengeWithFiltersQuery,
  type CoauthorIdeasQuery,
  type FilteredUsersQuery,
  GET_CHALLENGE_WITH_FILTERS,
  GET_CHALLENGES,
  GET_COAUTHOR_IDEAS,
  GET_COUNTRIES,
  GET_IDEAS_AND_COUNT,
  GET_USERFIELDS_FILTERED_USERIDS,
  type GetCountriesQuery,
  type GetIdeasAndCountQuery
} from '../../graphql/queries';
import { useUserFields, useWindowWidth } from '../../hooks';
import { CommunityContext } from '../../context';
import { ChallengeType } from '../../common/enums';
import { type IdeasAndCount } from '../../common/core-api';
import {
  ChartActionsRow,
  ChartScroll,
  ChartScrollInner,
  Grid,
  GridCol,
  GridHeader,
  GridRow,
  Pagination
} from './Analytics.styled';
import * as Styled from './components/SearchFilters.styled';
import { CustomFilters } from './CustomFilters';
import { type UserFieldType } from './types';
import { ChartExportMenu, IdeasTable, TableView } from './components';
import { EXPORT_TO_CSV_ENDPOINT, IDEAS_PER_PAGE } from './constants';
import { ChallengeSelectionType } from './enums';
import { accessibleChartTheme, getChartMinWidth, getChartScrollWidth } from './helpers/chartUtils';

export const IdeasByMilestoneStats = ({ dataTableViewEnabled }: { dataTableViewEnabled: boolean }) => {
  const intl = useIntl();
  const community = useContext(CommunityContext);
  const { data: communityUserFields } = useUserFields();
  const [userFieldOptions, setUserFieldOptions] = useState<UserFieldType>({});
  const [userFields, setUserFields] = useState<string>('{}');
  const [challenge, setChallenge] = useState<string>(ChallengeSelectionType.SELECT);
  const [milestone, setMilestone] = useState<number>();
  const [sort, setSort] = useState<string[][]>([['createdAt', 'DESC']]);
  const [offset, setOffset] = useState<number>(0);
  const [chartData, setChartData] = useState<ChallengeWithFiltersQuery>();
  const [ideasAndCount, setIdeasAndCount] = useState<IdeasAndCount | undefined>();
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const { data: countriesList } = useQuery<GetCountriesQuery>(GET_COUNTRIES, {
    variables: { id: community.id },
    fetchPolicy: 'cache-first'
  });

  useQuery<GetIdeasAndCountQuery>(GET_IDEAS_AND_COUNT, {
    variables: {
      challengeSelection: challenge,
      filters: { userFields },
      milestoneSelection: milestone,
      order: sort,
      offset: offset * 10
    },
    skip: challenge === ChallengeSelectionType.SELECT || milestone === undefined,
    onCompleted: (response) => {
      setIdeasAndCount(response?.ideasAndCount as IdeasAndCount);
    }
  });

  const { data: customFiltersUserIds } = useQuery<FilteredUsersQuery>(GET_USERFIELDS_FILTERED_USERIDS, {
    variables: { filters: { userFields } }
  });

  const { data: coauthorIdeaIds } = useQuery<CoauthorIdeasQuery>(GET_COAUTHOR_IDEAS, {
    variables: { customFiltersUserIds: customFiltersUserIds?.filteredUsers, filters: { userFields } }
  });

  const { loading: loadingChart } = useQuery<ChallengeWithFiltersQuery>(GET_CHALLENGE_WITH_FILTERS, {
    variables: {
      id: challenge,
      filters: { userFields }
    },
    onCompleted: (response) => {
      setChartData(response);
    }
  });

  // Used for getting the challenges dropdown
  const { loading: loadingMilestoneChallenges, data: milestoneChallengeList } = useQuery<ChallengesQuery>(
    GET_CHALLENGES,
    {
      variables: { order: ['title'], types: [ChallengeType.MILESTONE] },
      fetchPolicy: 'cache-first'
    }
  );

  const ideasLabel = intl.formatMessage({ id: 'ideas', defaultMessage: 'Ideas' });

  const windowWidth = useWindowWidth();
  const chartScrollWidth = useMemo(() => getChartScrollWidth(windowWidth), [windowWidth]);

  const ideasSort = (column: string) => {
    const [sortColumn, sortDirection] = sort[0];
    const newOrder = [[column, column === sortColumn && sortDirection === 'ASC' ? 'DESC' : 'ASC']];
    setSort(newOrder);
  };

  const getColumnSortDirection = (column: string) => {
    const [sortColumn, sortDirection] = sort[0];
    return sortColumn === column ? (sortDirection === 'DESC' ? 'descending' : 'ascending') : 'none';
  };

  const milestoneSummary = useMemo(() => {
    const milestoneArray = chartData?.challenge?.milestone?.milestones || [];
    const rejectedIdeaIds = chartData?.challenge?.milestone?.milestoneRejectedIdeaIds || [];

    const summary = milestoneArray.map((milestone) => {
      const ideas = milestone?.ideas || [];
      const rejectedForMilestone = ideas.filter((idea) => rejectedIdeaIds.includes(idea.id)).length;

      return {
        id: milestone.id,
        label: milestone.name,
        count: ideas.length - rejectedForMilestone,
        rejectedForMilestone
      };
    });

    const rejectedIdeasCount = summary.reduce((total, milestone) => total + milestone.rejectedForMilestone, 0);

    return [
      ...summary.map(({ rejectedForMilestone, ...milestone }) => milestone),
      {
        id: '0',
        label: 'Rejected',
        count: rejectedIdeasCount
      }
    ];
  }, [chartData]);

  const chartSeries = useMemo(
    () => [
      {
        id: ideasLabel,
        data: milestoneSummary.map((milestone) => ({
          x: milestone.label || '',
          y: milestone.count ?? 0,
          milestoneId: milestone.id
        }))
      }
    ],
    [ideasLabel, milestoneSummary]
  );

  const chartMinWidth = useMemo(() => getChartMinWidth(milestoneSummary.length), [milestoneSummary.length]);
  const formatDate = (date: Date) => date.toISOString().slice(0, 10);

  const tableColumns = useMemo(
    () => [
      { label: intl.formatMessage({ id: 'milestone', defaultMessage: 'Milestone' }) },
      { label: intl.formatMessage({ id: 'ideas', defaultMessage: 'Ideas' }) }
    ],
    [intl]
  );

  const buildTableRows = useCallback(() => {
    return milestoneSummary.map((milestone) => [milestone.label || '', milestone.count]);
  }, [milestoneSummary]);

  const tableRows = useMemo(() => {
    if (!dataTableViewEnabled) {
      return [];
    }

    return buildTableRows();
  }, [buildTableRows, dataTableViewEnabled]);

  const getExportData = useCallback(() => {
    return { columns: tableColumns, rows: buildTableRows() };
  }, [buildTableRows, tableColumns]);

  const exportData = () => {
    const exportUrl = new URL(EXPORT_TO_CSV_ENDPOINT + (challenge || '0').toString(), `https://${community.url}`);
    exportUrl.searchParams.append('includeUnpublished', '0');
    exportUrl.searchParams.append('includeComments', '0');
    milestone && exportUrl.searchParams.append('forMilestone', milestone.toString());
    milestone === 0 && exportUrl.searchParams.append('additionalFilters[rejected]', '1');

    if (Object.keys(userFieldOptions).length > 0) {
      exportUrl.searchParams.append('customFiltersUserIds', (customFiltersUserIds?.filteredUsers || []).toString());
      exportUrl.searchParams.append('ideaIds', (coauthorIdeaIds?.coauthorIdeas || []).toString());
    }

    window.location = exportUrl as unknown as Location;
  };

  if ((loadingMilestoneChallenges && !milestoneChallengeList) || (loadingChart && !chartData)) {
    return (
      <Styled.LoadingContainer>
        <LoadingIndicator inline />
      </Styled.LoadingContainer>
    );
  }

  return (
    <Grid key="ideasByMilestoneStats">
      <CustomFilters
        userFieldOptions={userFieldOptions}
        communityUserFields={communityUserFields}
        countries={countriesList?.community?.countries || []}
        userFieldUpdate={setUserFieldOptions}
      />
      <GridHeader key="challengeSelectionHeader">
        {intl.formatMessage({ id: 'show', defaultMessage: 'Show' })}
      </GridHeader>
      <GridRow key="challengeSelection">
        <Select
          defaultValue={challenge}
          options={[
            {
              label: intl.formatMessage({ id: 'selectAChallenge', defaultMessage: 'Select a challenge' }),
              value: ChallengeSelectionType.SELECT,
              'data-testid': `challege-${ChallengeSelectionType.SELECT}`
            },
            ...((milestoneChallengeList?.challenges || [])?.map((c) => ({
              label: c.title,
              value: c.id,
              'data-testid': `challege-${c.title}`
            })) || {})
          ]}
          onChange={(challengeSelection) => {
            setMilestone(undefined);
            setChallenge(challengeSelection.value);
            setUserFields(JSON.stringify(userFieldOptions));
            setIdeasAndCount(undefined);
          }}
        />
      </GridRow>
      <GridRow key="updateButton">
        <Button
          priority="secondary"
          onClick={() => {
            setMilestone(undefined);
            setUserFields(JSON.stringify(userFieldOptions));
            setIdeasAndCount(undefined);
          }}
          disabled={challenge === ChallengeSelectionType.SELECT}
        >
          {intl.formatMessage({ id: 'update', defaultMessage: 'Update' })}
        </Button>
      </GridRow>
      {challenge !== ChallengeSelectionType.SELECT && chartData && !dataTableViewEnabled && (
        <ChartActionsRow key="ideasByMilestoneExport">
          <ChartExportMenu
            chartRef={chartContainerRef}
            fileName={intl.formatMessage({ id: 'ideasByMilestone', defaultMessage: 'Ideas by Milestone' })}
            formatDate={formatDate}
            getExportData={getExportData}
          />
        </ChartActionsRow>
      )}
      <GridRow key="chart">
        {challenge !== ChallengeSelectionType.SELECT &&
          chartData &&
          (dataTableViewEnabled ? (
            <TableView
              columns={tableColumns}
              rows={tableRows}
              formatDate={formatDate}
              onRowClick={(index: number) => {
                const milestoneId = milestoneSummary[index]?.id;
                if (milestoneId !== undefined) {
                  setMilestone(Number(milestoneId));
                  setOffset(0);
                }
              }}
              summary={intl.formatMessage({
                id: 'ideasByMilestone',
                defaultMessage: 'Ideas by Milestone'
              })}
            />
          ) : (
            <ThemeProvider theme={accessibleChartTheme}>
              <ChartScroll width={chartScrollWidth}>
                <ChartScrollInner minWidth={chartMinWidth} ref={chartContainerRef}>
                  <BarChart
                    axisX={{ label: intl.formatMessage({ id: 'milestone', defaultMessage: 'Milestone' }) }}
                    axisY={{
                      formatScreenReaderValue: (value) => {
                        return `${ideasLabel}: ${value}`;
                      },
                      label: intl.formatMessage({
                        id: 'numberOfIdeasAtThisMilestone',
                        defaultMessage: 'Number of ideas at this milestone'
                      }),
                      formatValue: (value) => {
                        const numericValue = Number(value);
                        return Number.isInteger(numericValue) ? numericValue : '';
                      }
                    }}
                    data={chartSeries}
                    domainScaling="from-zero"
                    hasLegend={false}
                    hasValueLabels
                    onDataClick={(dataPoint) => {
                      if (dataPoint?.milestoneId === undefined) {
                        return;
                      }

                      setOffset(0);
                      setMilestone(Number(dataPoint.milestoneId));
                    }}
                  />
                </ChartScrollInner>
              </ChartScroll>
            </ThemeProvider>
          ))}
      </GridRow>
      {challenge !== ChallengeSelectionType.SELECT && ideasAndCount && ideasAndCount.count > 0 ? (
        <>
          <GridRow key="table" justify="center">
            <GridCol>
              <IdeasTable
                getColumnSortDirection={getColumnSortDirection}
                ideasAndCount={ideasAndCount || { count: 0, rows: [] }}
                intl={intl}
                sortIdeas={ideasSort}
              />
            </GridCol>
            <GridCol>
              <Pagination
                numPages={ideasAndCount?.count && Math.ceil(ideasAndCount.count / IDEAS_PER_PAGE)}
                activeIndex={offset}
                onPageClick={(page) => {
                  setOffset(page.index as number);
                }}
              />
            </GridCol>
          </GridRow>
          <GridRow key="exportData" justify="start">
            <GridCol>
              <Button priority="secondary" onClick={exportData}>
                {intl.formatMessage({ id: 'exportData', defaultMessage: 'Export Data' })}
              </Button>
            </GridCol>
          </GridRow>
        </>
      ) : (
        chartData?.challenge &&
        ideasAndCount?.count === 0 && (
          <ErrorMessage
            message={intl.formatMessage({ id: 'noIdeasWereFound', defaultMessage: 'No ideas were found' })}
            details={intl.formatMessage({
              id: 'pleaseAdjustYourSearch',
              defaultMessage: 'Please adjust your search parameters and try again.'
            })}
          />
        )
      )}
    </Grid>
  );
};
